/**
 * MessageBubble - iMessage-style bubble with reactions, replies, and long-press menu.
 */

import { useState, useRef, useCallback } from 'react'
import { useAuth } from '../../../features/auth'
import { useGroupChat, type GroupChatMessage } from '../GroupChatContext'

interface MessageBubbleProps {
  message: GroupChatMessage
  showUsername: boolean
}

const QUICK_REACTIONS = ['❤️', '👍', '😂', '🔥', '😮', '😢', '🎉', '💯']

function usernameColor(username: string): string {
  const colors = ['#db2777', '#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#ea580c', '#0d9488', '#9333ea']
  let hash = 0
  for (let i = 0; i < username.length; i++) hash = username.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000)
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 0) return time
  if (diffDays === 1) return `Yesterday ${time}`
  if (diffDays < 7) return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`
}

export function MessageBubble({ message, showUsername }: MessageBubbleProps) {
  const { user } = useAuth()
  const { addReaction, removeReaction, setReplyingTo } = useGroupChat()
  const isOwn = user?.id === message.user_id

  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)

  // Position menu anchored to the message bubble
  const openMenu = useCallback(() => {
    const rect = bubbleRef.current?.getBoundingClientRect()
    if (!rect) return
    // Place menu above the message, aligned to the bubble side
    const x = isOwn ? rect.right - 270 : rect.left
    const y = rect.top - 10
    setMenuPos({
      x: Math.max(8, Math.min(x, window.innerWidth - 278)),
      y: Math.max(8, Math.min(y, window.innerHeight - 200))
    })
  }, [isOwn])

  // Long-press (mobile)
  const handleTouchStart = useCallback(() => {
    longPressTimer.current = setTimeout(openMenu, 500)
  }, [openMenu])

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }, [])

  // Right-click (desktop)
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    openMenu()
  }, [openMenu])

  const handleReaction = (emoji: string) => {
    const existing = message.reactions.find(r => r.user_id === user?.id && r.reaction === emoji)
    if (existing) removeReaction(message.id, emoji)
    else addReaction(message.id, emoji)
    setMenuPos(null)
  }

  const handleReply = () => {
    setReplyingTo({ id: message.id, text: message.content, username: message.user.chat_username })
    setMenuPos(null)
  }

  // Group reactions by emoji
  const groupedReactions = message.reactions.reduce<Record<string, { count: number; hasOwn: boolean; users: string[] }>>((acc, r) => {
    if (!acc[r.reaction]) acc[r.reaction] = { count: 0, hasOwn: false, users: [] }
    acc[r.reaction].count++
    acc[r.reaction].users.push(r.username)
    if (r.user_id === user?.id) acc[r.reaction].hasOwn = true
    return acc
  }, {})

  const isAdminMessage = message.message_type === 'admin'
  const adminDisplayName = (message.metadata as Record<string, unknown>)?.admin_display_name as string || message.user.chat_username
  const isMediaMessage = message.message_type === 'media'
  const mediaData = (message.metadata as Record<string, unknown>)?.media as Record<string, string> | undefined

  // System messages
  if (message.message_type === 'system') {
    return (
      <div className="text-center py-1">
        <span className="text-gray-400 text-xs bg-white/60 px-3 py-1 rounded-full">{message.content}</span>
      </div>
    )
  }

  // Admin (Caryn) messages — distinct pink-bordered bubble, always left-aligned
  if (isAdminMessage) {
    return (
      <div className={`flex justify-start ${showUsername ? 'mt-4' : 'mt-1'}`}>
        <div className="max-w-[85%]">
          <div className="flex items-center gap-2 mb-1 ml-3">
            <span className="text-[11px] font-bold text-pink-600">
              ✦ {adminDisplayName}
            </span>
            <span className="text-[10px] text-gray-400">{formatTime(message.created_at)}</span>
          </div>
          <div className="bg-pink-50 border-2 border-pink-400 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed text-gray-800">
            {message.content}
          </div>
          {/* Reactions */}
          {Object.keys(groupedReactions).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1 ml-1">
              {Object.entries(groupedReactions).map(([emoji, data]) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    data.hasOwn ? 'bg-pink-100 border-pink-300 text-pink-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{emoji}</span>
                  {data.count > 1 && <span className="text-[10px]">{data.count}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Deleted messages
  if (message.is_deleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${showUsername ? 'mt-3' : 'mt-1'}`}>
        <div className="max-w-[80%]">
          {showUsername && !isOwn && (
            <p className="text-[11px] font-medium mb-0.5 ml-3" style={{ color: usernameColor(message.user.chat_username) }}>
              {message.user.chat_username}
            </p>
          )}
          <div className="bg-gray-100 rounded-2xl px-4 py-2">
            <p className="text-gray-400 text-sm italic">{message.content}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Context menu - anchored next to the message */}
      {menuPos && (
        <div className="fixed inset-0 z-[9999]" onClick={() => setMenuPos(null)}>
          <div
            className="fixed bg-white rounded-2xl shadow-xl p-2.5 border border-pink-100"
            style={{ left: menuPos.x, top: menuPos.y, width: 270 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Quick reactions - horizontally scrollable */}
            <div className="overflow-x-auto mb-2 -mx-1 px-1 scrollbar-hide">
              <div className="flex gap-1.5 w-max">
                {QUICK_REACTIONS.map((emoji) => {
                  const hasOwn = groupedReactions[emoji]?.hasOwn
                  return (
                    <button
                      key={emoji}
                      onClick={() => handleReaction(emoji)}
                      className={`text-lg w-9 h-9 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${hasOwn ? 'bg-pink-100 scale-110' : 'hover:bg-gray-100 active:bg-gray-200'}`}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="border-t border-gray-100 pt-1">
              {/* Reply */}
              <button
                onClick={handleReply}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                Reply
              </button>
              {/* Report */}
              {(
                <button
                  onClick={() => {
                    const token = localStorage.getItem('auth_token')
                    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
                    fetch(`${apiUrl}/api/groupchat/report`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ messageId: message.id, reason: 'Reported by user' })
                    }).catch(() => {})
                    setMenuPos(null)
                    alert('Message reported. An admin will review it.')
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        ref={bubbleRef}
        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${showUsername ? 'mt-3' : 'mt-1'}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onContextMenu={handleContextMenu}
      >
        <div className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'}`}>
          {/* Username + timestamp */}
          {showUsername && !isOwn && (
            <div className="flex items-center gap-2 mb-0.5 ml-3">
              <span className="text-[11px] font-semibold" style={{ color: usernameColor(message.user.chat_username) }}>
                {message.user.chat_username}
              </span>
              <span className="text-[10px] text-gray-400">{formatTime(message.created_at)}</span>
            </div>
          )}
          {showUsername && isOwn && (
            <div className="flex justify-end mb-0.5 mr-3">
              <span className="text-[10px] text-gray-400">{formatTime(message.created_at)}</span>
            </div>
          )}

          {/* Reply preview */}
          {message.reply_to && (
            <div
              className={`flex items-start gap-1.5 mb-1 ${isOwn ? 'mr-2 justify-end' : 'ml-2'}`}
              onClick={() => {
                const el = document.querySelector(`[data-message-id="${message.reply_to?.id}"]`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  el.classList.add('bg-pink-100')
                  setTimeout(() => el.classList.remove('bg-pink-100'), 2000)
                }
              }}
            >
              <div className={`max-w-[90%] px-3 py-1.5 rounded-lg text-xs cursor-pointer ${isOwn ? 'bg-pink-200/60 text-pink-800' : 'bg-gray-100 text-gray-600'}`}>
                <span className="font-semibold">{message.reply_to.username}</span>
                <p className="truncate opacity-80">{message.reply_to.text}</p>
              </div>
            </div>
          )}

          {/* Message bubble */}
          <div
            data-message-id={message.id}
            className={`transition-colors duration-500 ${
              isMediaMessage && mediaData?.type === 'gif'
                ? 'rounded-2xl overflow-hidden'
                : `px-4 py-2.5 text-sm leading-relaxed break-words ${
                    isOwn
                      ? 'bg-pink-500 text-white rounded-2xl rounded-br-md'
                      : 'bg-white text-gray-800 rounded-2xl rounded-bl-md shadow-sm border border-pink-100'
                  }`
            }`}
          >
            {isMediaMessage && mediaData?.type === 'gif' ? (
              <img
                src={mediaData.url || mediaData.thumbnail_url}
                alt={mediaData.title || 'GIF'}
                className="max-w-[250px] max-h-[250px] rounded-2xl object-cover"
                loading="lazy"
              />
            ) : isMediaMessage && mediaData?.type === 'image' ? (
              <img
                src={mediaData.url}
                alt="Image"
                className="max-w-[300px] max-h-[300px] rounded-lg object-cover"
                loading="lazy"
              />
            ) : isMediaMessage && mediaData?.type === 'voice' ? (
              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl ${isOwn ? 'bg-pink-500 text-white rounded-br-md' : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-pink-100'}`}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                </svg>
                <audio controls src={mediaData.url} className="h-8 max-w-[200px]" />
              </div>
            ) : (
              message.content
            )}
          </div>

          {/* Reactions display */}
          {Object.keys(groupedReactions).length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}>
              {Object.entries(groupedReactions).map(([emoji, data]) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    data.hasOwn
                      ? 'bg-pink-100 border-pink-300 text-pink-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={data.users.join(', ')}
                >
                  <span>{emoji}</span>
                  {data.count > 1 && <span className="text-[10px]">{data.count}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
